import React, { memo } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  BarChart3,
  Users as UsersIcon,
  CreditCard,
  FileText,
  PauseCircle,
} from "lucide-react";
import { Badge } from "../../../components/ui";

const LUCIDE = {
  BarChart3,
  UsersIcon,
  CreditCard,
  FileText,
  PauseCircle,
};

const renderIcon = (icon, isActive) => {
  if (!icon) return null;
  if (icon.lucide) {
    const Lc = LUCIDE[icon.lucide];
    return Lc ? (
      <Lc size={18} className="sb-sidebar__row-icon-svg" aria-hidden />
    ) : null;
  }
  const src = isActive ? icon.active : icon.inactive;
  if (!src) return null;
  return <img src={src} alt="" className="sb-sidebar__row-icon-img" />;
};

const resolveBadge = (badge, counters) => {
  if (!badge || !counters) return null;
  const value = counters[badge];
  if (!value || value <= 0) return null;
  return value;
};

const MenuItem = ({ item, ctx, depth = 0 }) => {
  const {
    can,
    canAny,
    isMatch,
    isExpanded,
    toggleSection,
    counters,
    onSubItemNavigate,
    handlers,
  } = ctx;

  // Visibility
  if (item.permission && !can(item.permission)) return null;
  if (item.anyPermission && !canAny(item.anyPermission)) return null;

  const active = isMatch(item.matches);
  const badgeValue = resolveBadge(item.badge, counters);

  // Nested accordion
  if (item.type === "accordion") {
    const expanded = isExpanded(item.key);
    const visibleItems = (item.items || []).filter((sub) => {
      if (sub.permission) return can(sub.permission);
      if (sub.anyPermission) return canAny(sub.anyPermission);
      return true;
    });
    if (visibleItems.length === 0) return null;

    return (
      <div
        className={
          active
            ? "sb-sidebar__group sb-sidebar__group--nested sb-sidebar__group--active"
            : "sb-sidebar__group sb-sidebar__group--nested"
        }
        data-depth={depth}
      >
        <button
          type="button"
          className={
            active
              ? "sb-sidebar__row sb-sidebar__row--header sb-sidebar__row--active"
              : "sb-sidebar__row sb-sidebar__row--header"
          }
          onClick={() => toggleSection(item.key)}
          aria-expanded={expanded}
        >
          <span className="sb-sidebar__row-icon">
            {renderIcon(item.icon, active)}
          </span>
          <span className="sb-sidebar__row-label" title={item.label}>{item.label}</span>
          {badgeValue !== null && (
            <Badge variant="danger" size="sm" className="sb-sidebar__row-badge">
              {badgeValue}
            </Badge>
          )}
          <ChevronDown
            size={14}
            className={
              expanded
                ? "sb-sidebar__row-chevron sb-sidebar__row-chevron--open"
                : "sb-sidebar__row-chevron"
            }
            aria-hidden
          />
        </button>

        {expanded && (
          <div className="sb-sidebar__group-body">
            {visibleItems.map((sub) => (
              <MenuItem
                key={sub.key || `${item.key}-${sub.to || sub.label}`}
                item={sub}
                ctx={ctx}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Handler-driven row (e.g., Workflow Task Form)
  if (item.handler) {
    const handler = handlers && handlers[item.handler];
    return (
      <button
        type="button"
        className={
          active
            ? "sb-sidebar__row sb-sidebar__row--leaf sb-sidebar__row--active"
            : "sb-sidebar__row sb-sidebar__row--leaf"
        }
        onClick={() => {
          onSubItemNavigate({ socketTrigger: item.socketTrigger });
          if (typeof handler === "function") handler();
        }}
        data-depth={depth}
      >
        <span className="sb-sidebar__row-icon">
          {renderIcon(item.icon, active)}
        </span>
        <span className="sb-sidebar__row-label" title={item.label}>{item.label}</span>
        {badgeValue !== null && (
          <Badge variant="danger" size="sm" className="sb-sidebar__row-badge">
            {badgeValue}
          </Badge>
        )}
      </button>
    );
  }

  // Plain link
  return (
    <Link
      to={item.to || "#"}
      className={
        active
          ? "sb-sidebar__row sb-sidebar__row--leaf sb-sidebar__row--active"
          : "sb-sidebar__row sb-sidebar__row--leaf"
      }
      onClick={() => onSubItemNavigate({ socketTrigger: item.socketTrigger })}
      data-depth={depth}
    >
      <span className="sb-sidebar__row-icon">
        {renderIcon(item.icon, active)}
      </span>
      <span className="sb-sidebar__row-label" title={item.label}>{item.label}</span>
      {badgeValue !== null && (
        <Badge variant="danger" size="sm" className="sb-sidebar__row-badge">
          {badgeValue}
        </Badge>
      )}
    </Link>
  );
};

export default memo(MenuItem);
